import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import { shouldDropExceptionEvent } from './exceptionFilter';

export { posthog };

let initialized = false;

const POSTHOG_PROXY_HOST = 'https://scoutingdept.dugoutdesk.ca';
const POSTHOG_DIRECT_HOST = 'https://us.i.posthog.com';
const POSTHOG_UI_HOST = 'https://us.posthog.com';

/** Initialize PostHog when key is set. Call once at app boot (e.g. main.tsx). */
export function initPosthog() {
  // Vite only exposes VITE_* to the client; use VITE_POSTHOG_KEY in .env and Render
  const key = (import.meta.env.VITE_POSTHOG_KEY as string) || '';
  if (!key) return;

  const defaultHost = import.meta.env.PROD ? POSTHOG_PROXY_HOST : POSTHOG_DIRECT_HOST;
  let host = (import.meta.env.VITE_POSTHOG_HOST as string) || defaultHost;
  if (import.meta.env.PROD && /^(https?:\/\/)?(127\.0\.0\.1|localhost)(:\d+)?/i.test(host.trim())) {
    host = POSTHOG_PROXY_HOST;
  }
  posthog.init(key, {
    api_host: host,
    ui_host: POSTHOG_UI_HOST,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_exceptions: true,
    // Drop stale-asset and third-party recorder faults before they reach error
    // tracking. Covers window errors and manual captureException alike.
    before_send: event => (shouldDropExceptionEvent(event) ? null : event),
    // Session replay off by default — public schedule traffic burns free-tier recordings.
    // Registration + coaches opt in via startRegistrationRecording / startCoachRecording.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: false,
      // PII on registration forms: email/phone/postal/roster use class `ph-mask`.
      maskInputOptions: { password: true, email: true },
      maskTextSelector: '.ph-mask',
    },
  });
  initialized = true;

  // One test event per load so you can confirm connection in PostHog → Activity / Live events (dev and prod)
  posthog.capture('posthog_connection_test', {
    source: 'dugout_desk_init',
    env: import.meta.env.DEV ? 'development' : 'production',
  });

  // Expose for manual verification in console: window.__posthogDebug() (dev and prod)
  if (typeof window !== 'undefined') {
    (window as unknown as { __posthogDebug?: () => void }).__posthogDebug =
      function posthogDebug() {
        if (!initialized) {
          console.warn(
            'PostHog not initialized. In production, set VITE_POSTHOG_KEY (and VITE_POSTHOG_HOST) in Render → Environment and redeploy so the key is baked into the client build.'
          );
          return;
        }
        posthog.capture('posthog_manual_test', { at: new Date().toISOString() });
        console.log('PostHog: test event sent. Check PostHog → Activity / Live events.');
      };
  }
}

/**
 * Identify the authenticated admin/org user (server-session path via /api/auth/me).
 * For coaches authenticating via /magic, use `setCoachIdentity` instead.
 */
export function setPosthogUser(
  user: {
    id: string | number;
    email?: string | null;
    name?: string | null;
    role?: string | null;
    organizationId?: string | null;
    teamId?: string | null;
    teamName?: string | null;
    tournamentId?: string | null;
  } | null
) {
  if (!initialized) return;
  if (!user) {
    posthog.reset();
    return;
  }
  posthog.identify(String(user.id), {
    email: user.email ?? undefined,
    name: user.name ?? undefined,
    role: user.role ?? undefined,
    org_id: user.organizationId ?? undefined,
    team_id: user.teamId ?? undefined,
    team_name: user.teamName ?? undefined,
    tournament_id: user.tournamentId ?? undefined,
  });
}

/**
 * Identify a coach authenticating via the /magic token flow.
 * Coaches do not have a `users` row, so we use the team id as the PostHog distinct_id
 * (team-level person, per the chosen strategy).
 */
export function setCoachIdentity(coach: {
  teamId: string;
  teamName?: string | null;
  tournamentId?: string | null;
  tournamentName?: string | null;
  organizationId?: string | null;
  email?: string | null;
}) {
  if (!initialized) return;
  posthog.identify(coach.teamId, {
    email: coach.email ?? undefined,
    role: 'coach',
    team_id: coach.teamId,
    team_name: coach.teamName ?? undefined,
    tournament_id: coach.tournamentId ?? undefined,
    tournament_name: coach.tournamentName ?? undefined,
    org_id: coach.organizationId ?? undefined,
  });
}

/** Clear the PostHog identity (call on sign-out). */
export function clearIdentity() {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch {
    // never throw from observability
  }
}

let coachRecordingActive = false;
let registrationRecordingActive = false;

export function startCoachRecording() {
  if (!initialized) return;
  try {
    posthog.startSessionRecording();
    coachRecordingActive = true;
  } catch {
    // never throw from observability
  }
}

export function stopCoachRecording() {
  if (!initialized) return;
  coachRecordingActive = false;
  try {
    posthog.stopSessionRecording();
  } catch {
    // never throw from observability
  }
}

const REGISTRATION_RECORDING_SAMPLE_RATE = 0.1;
const registrationRecordingSessionKey = 'dd_reg_recording_sample';
/** One-shot: next registration page unmount should not stop recording. */
const registrationFunnelContinueKey = 'dd_reg_funnel_continue';

export type RegistrationRecordingContext = {
  /** Hub campaign vs single-tournament wizard. */
  registrationSurface: 'hub' | 'tournament' | 'status' | 'success';
  campaignSlug?: string | null;
  tournamentId?: string | null;
  registrationMode?: string | null;
};

function isRegistrationSampled(): boolean {
  if (typeof window === 'undefined') return false;
  let sample = sessionStorage.getItem(registrationRecordingSessionKey);
  if (sample == null) {
    sample = Math.random() < REGISTRATION_RECORDING_SAMPLE_RATE ? '1' : '0';
    sessionStorage.setItem(registrationRecordingSessionKey, sample);
  }
  return sample === '1';
}

/**
 * Sampled session replay for registration hubs / wizards / status / success.
 * Avoids burning free-tier quota on public schedule traffic.
 * Sample decision is sticky per browser tab (sessionStorage).
 * Call `markRegistrationFunnelContinue()` before in-app navigations so cleanup
 * does not stop the recording mid-funnel.
 */
export function startRegistrationRecording(ctx?: RegistrationRecordingContext) {
  if (!initialized || typeof window === 'undefined') return;
  try {
    registerSurface('public');
    if (ctx) {
      posthog.register({
        registration_surface: ctx.registrationSurface,
        ...(ctx.campaignSlug ? { campaign_slug: ctx.campaignSlug } : {}),
        ...(ctx.tournamentId ? { tournament_id: ctx.tournamentId } : {}),
        ...(ctx.registrationMode ? { registration_mode: ctx.registrationMode } : {}),
      });
    }
    if (!isRegistrationSampled() || coachRecordingActive) return;
    posthog.startSessionRecording();
    registrationRecordingActive = true;
  } catch {
    // never throw from observability
  }
}

/** Keep recording across Hub → `/tournaments/:id/register` (or status) navigations. */
export function markRegistrationFunnelContinue() {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(registrationFunnelContinueKey, '1');
  } catch {
    // ignore
  }
}

/**
 * Stop recording on unmount unless `markRegistrationFunnelContinue()` was called
 * for the next hop. Pass `{ force: true }` to end regardless.
 * Only stops replay this module started — never a coach session already recording.
 */
export function stopRegistrationRecording(opts?: { force?: boolean }) {
  if (!initialized || typeof window === 'undefined') return;
  try {
    if (!opts?.force) {
      const continuing = sessionStorage.getItem(registrationFunnelContinueKey) === '1';
      if (continuing) {
        sessionStorage.removeItem(registrationFunnelContinueKey);
        return;
      }
    }
    sessionStorage.removeItem(registrationFunnelContinueKey);
    if (!registrationRecordingActive) return;
    registrationRecordingActive = false;
    if (coachRecordingActive) return;
    posthog.stopSessionRecording();
  } catch {
    // never throw from observability
  }
}

/** End the registration recording funnel (success settled / explicit leave). */
export function endRegistrationRecording() {
  stopRegistrationRecording({ force: true });
}

/**
 * Soft-identify a coach who typed a valid email on a registration hub/wizard
 * before submit — enables PostHog person profiles for abandon analysis.
 * Distinct id is the lowercased email (stable across sessions).
 */
export function identifyRegistrationLead(lead: {
  email: string;
  campaignSlug?: string | null;
  tournamentId?: string | null;
}) {
  if (!initialized) return;
  const email = lead.email.trim().toLowerCase();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
  try {
    posthog.identify(email, {
      email,
      role: 'registration_lead',
      ...(lead.campaignSlug ? { campaign_slug: lead.campaignSlug } : {}),
      ...(lead.tournamentId ? { tournament_id: lead.tournamentId } : {}),
    });
  } catch {
    // never throw from observability
  }
}

/** Tag subsequent events with the active app surface (public / coach / admin). */
export function registerSurface(surface: 'public' | 'coach' | 'admin') {
  if (!initialized) return;
  try {
    posthog.register({ app_surface: surface });
  } catch {
    // never throw from observability
  }
}

/** Capture an exception for PostHog error tracking. Safe before init. */
export function captureClientException(error: unknown, props?: Record<string, unknown>) {
  try {
    if (!initialized) return;
    // The init before_send filter drops stale-asset and third-party recorder
    // faults from this path too, so no manual guard is needed here.
    posthog.captureException(error, props);
  } catch {
    // never throw from observability
  }
}

/**
 * Capture an analytics event in PostHog and drop a Sentry breadcrumb so any
 * subsequent crash carries the same trail. Safe to call before init: missing
 * PostHog key will simply skip the capture without throwing.
 */
export function track(event: string, props?: Record<string, unknown>) {
  try {
    if (initialized) {
      posthog.capture(event, props);
    }
  } catch {
    // never throw from observability
  }
  try {
    Sentry.addBreadcrumb({
      category: 'event',
      message: event,
      data: props,
      level: 'info',
    });
  } catch {
    // never throw from observability
  }
}
